import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { useBooking } from "@/components/site/BookingProvider";

const NAV = [
  { label: "Technology", href: "#technology" },
  { label: "Membership", href: "#membership" },
  { label: "Stories", href: "#stories" },
  { label: "FAQ", href: "#faq" },
  { label: "Visit", href: "#visit" },
];

const LOGO_BLUE =
  "https://images.leadconnectorhq.com/image/f_webp/q_80/r_320/u_https://assets.cdn.filesafe.space/UL9GAEU0ONW7drLQGkjG/media/685dc3a52e1e3a8273bc1a1c.png";
const LOGO_WHITE =
  "https://images.leadconnectorhq.com/image/f_webp/q_80/r_320/u_https://assets.cdn.filesafe.space/UL9GAEU0ONW7drLQGkjG/media/685dbe808f84bd026b3f6756.png";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { openBooking } = useBooking();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Light text when over the dark hero (top, not scrolled). Dark text when scrolled past hero.
  const isLight = !scrolled;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-bone/90 backdrop-blur-xl border-b border-sand"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="brand-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src={isLight ? LOGO_WHITE : LOGO_BLUE}
            alt="Maui Longevity Lounge"
            className="h-9 sm:h-10 w-auto transition-opacity duration-300"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={`mll-link text-[13px] uppercase tracking-[0.18em] font-semibold transition-colors ${
                isLight
                  ? "text-bone/95 hover:text-clay-soft"
                  : "text-stone/80 hover:text-jungle"
              }`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="tel:8085008857"
            data-testid="header-phone"
            className={`text-[13px] tracking-wide inline-flex items-center gap-2 transition-colors ${
              isLight
                ? "text-bone/85 hover:text-clay-soft"
                : "text-stone/70 hover:text-jungle"
            }`}
          >
            <Phone className="w-3.5 h-3.5" /> 808.500.8857
          </a>
          <button
            type="button"
            onClick={openBooking}
            data-testid="header-book-button"
            className={`inline-flex items-center px-5 h-11 text-[12px] uppercase tracking-[0.18em] font-semibold transition-colors ${
              isLight
                ? "bg-bone text-stone hover:bg-clay-soft hover:text-bone"
                : "bg-jungle text-bone hover:bg-jungle-dark"
            }`}
          >
            Book a Session
          </button>
        </div>

        <button
          aria-label="Open menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden inline-flex items-center justify-center w-10 h-10 transition-colors ${
            isLight ? "text-bone" : "text-stone"
          }`}
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-bone border-t border-sand"
        >
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="text-sm uppercase tracking-[0.18em] font-semibold text-stone"
              >
                {n.label}
              </a>
            ))}
            <a
              href="tel:8085008857"
              className="text-sm uppercase tracking-[0.18em] font-semibold text-stone inline-flex items-center gap-2"
            >
              <Phone className="w-4 h-4" /> 808.500.8857
            </a>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                openBooking();
              }}
              data-testid="mobile-book-button"
              className="mt-2 inline-flex items-center justify-center bg-jungle text-bone px-6 h-12 text-xs uppercase tracking-[0.18em] font-semibold"
            >
              Book a Session
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
